import { Card, Form, Input, Button, Typography, message, Result, Spin } from 'antd';
import { LockOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authApi } from '../../../api/auth.api';

const { Text } = Typography;

const AuthCard = ({ children }) => (
  <div style={{
    minHeight: '100vh',
    background: 'linear-gradient(135deg, var(--wl-primary-pale) 0%, var(--wl-bg) 50%, var(--wl-primary-pale) 100%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
  }}>
    <div style={{ position: 'fixed', top: -80, right: -80, width: 300, height: 300, borderRadius: '50%', background: 'rgba(77,184,71,0.15)', pointerEvents: 'none' }} />
    <div style={{ position: 'fixed', bottom: -60, left: -60, width: 220, height: 220, borderRadius: '50%', background: 'rgba(255,255,255,0.08)', pointerEvents: 'none' }} />
    <Card
      bordered={false}
      style={{ width: 440, borderRadius: 24, boxShadow: '0 24px 80px rgba(0,0,0,0.25)', overflow: 'hidden', border: 'none' }}
      styles={{ body: { padding: 0 } }}
    >
      {/* Logo header */}
      <div style={{ background: 'var(--wl-primary-pale)', padding: '32px 40px 28px', textAlign: 'center' }}>
        <img
          src="/logo/WECIRCLE_LOGO with Leaf.svg"
          alt="WeLittle"
          style={{ maxHeight: 52, maxWidth: 220, objectFit: 'contain'}}
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
        <p style={{ marginTop: 10, color: 'rgba(255,255,255,0.80)', fontSize: 13, letterSpacing: 0.3 }}>
          WeCircle
        </p>
      </div>
      {children}
    </Card>
  </div>
);

const passwordRules = [
  { required: true, message: 'Please enter a new password' },
  { min: 8, message: 'Password must be at least 8 characters' },
  {
    validator: (_, value) => {
      if (!value) return Promise.resolve();
      if (!/[a-zA-Z]/.test(value)) return Promise.reject('Password must contain at least one letter');
      if (!/\d/.test(value))       return Promise.reject('Password must contain at least one number');
      return Promise.resolve();
    },
  },
];

const ResetPassword = () => {
  const [loading,  setLoading]  = useState(false);
  const [success,  setSuccess]  = useState(false);
  const [countdown, setCountdown] = useState(5);
  const navigate    = useNavigate();
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm();

  const token = searchParams.get('token') || '';

  // Countdown redirect after success
  useEffect(() => {
    if (!success) return;
    if (countdown <= 0) { navigate('/login'); return; }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [success, countdown, navigate]);

  const handleSubmit = async ({ password, confirmPassword }) => {
    setLoading(true);
    try {
      await authApi.resetPassword({ token, password, confirmPassword });
      setSuccess(true);
    } catch (err) {
      message.error(err.message || 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  // No token in URL
  if (!token) {
    return (
      <AuthCard>
        <div style={{ padding: '40px 40px 32px' }}>
          <Result
            status="error"
            title={<span style={{ color: 'var(--wl-text)', fontSize: 18, fontWeight: 700 }}>Invalid Reset Link</span>}
            subTitle="This password reset link is missing or invalid. Please request a new one."
            extra={
              <Button
                type="primary"
                onClick={() => navigate('/forgot-password')}
                style={{ background: 'linear-gradient(135deg,var(--wl-primary-light),var(--wl-primary))', border: 'none', borderRadius: 9999 }}
              >
                Request New Link
              </Button>
            }
          />
        </div>
      </AuthCard>
    );
  }

  // Success state
  if (success) {
    return (
      <AuthCard>
        <div style={{ padding: '40px 40px 32px' }}>
          <Result
            icon={
              <div style={{
                width: 72, height: 72, borderRadius: '50%',
                background: 'linear-gradient(135deg,#52c41a,#389e0d)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 16px',
              }}>
                <CheckCircleOutlined style={{ fontSize: 36, color: '#fff' }} />
              </div>
            }
            title={<span style={{ color: 'var(--wl-text)', fontSize: 20, fontWeight: 700 }}>Password Reset!</span>}
            subTitle={
              <span style={{ color: '#666', fontSize: 14, lineHeight: 1.7 }}>
                Your password has been updated successfully.<br />
                Redirecting to login in <strong style={{ color: 'var(--wl-primary)' }}>{countdown}s</strong>…
              </span>
            }
          />
          <Button
            type="primary"
            block
            onClick={() => navigate('/login')}
            style={{
              height: 48, borderRadius: 9999, fontSize: 15,
              background: 'linear-gradient(181deg,var(--wl-primary-light) 0%,var(--wl-primary) 100%)',
              border: 'none', boxShadow: '0 6px 20px rgba(var(--wl-primary-rgb),0.4)',
            }}
          >
            Go to Login
          </Button>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard>
      <div style={{ padding: '36px 40px 28px' }}>
        <p style={{ fontFamily: "'Fredoka One',sans-serif", fontSize: 20, color: 'var(--wl-primary)', marginBottom: 6 }}>
          Set New Password
        </p>
        <p style={{ fontSize: 13, color: '#888', marginBottom: 28, lineHeight: 1.6 }}>
          Choose a strong password — min 8 characters with at least one letter and one number.
        </p>

        <Form form={form} layout="vertical" onFinish={handleSubmit} size="large">
          <Form.Item name="password" label="New Password" rules={passwordRules}>
            <Input.Password
              prefix={<LockOutlined style={{ color: 'var(--wl-text-muted)' }} />}
              placeholder="Enter new password"
              style={{ borderRadius: 12 }}
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Confirm New Password"
            dependencies={['password']}
            rules={[
              { required: true, message: 'Please confirm your password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) return Promise.resolve();
                  return Promise.reject('Passwords do not match');
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: 'var(--wl-text-muted)' }} />}
              placeholder="Re-enter new password"
              style={{ borderRadius: 12 }}
            />
          </Form.Item>

          {/* Password hint */}
          <div style={{
            background: 'var(--wl-primary-pale)', border: '1px solid var(--wl-primary-pale)', borderRadius: 10,
            padding: '10px 14px', marginBottom: 20, fontSize: 12, color: 'var(--wl-primary-light)',
          }}>
            Password must be at least <strong>8 characters</strong> and contain
            at least one <strong>letter</strong> and one <strong>number</strong>.
          </div>

          <Form.Item style={{ marginBottom: 8 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
              style={{
                height: 48, borderRadius: 9999, fontSize: 15,
                fontFamily: "'Fredoka One',sans-serif", letterSpacing: 0.5,
                background: 'linear-gradient(181deg,var(--wl-primary-light) 0%,var(--wl-primary) 100%)',
                border: 'none', boxShadow: '0 6px 20px rgba(var(--wl-primary-rgb),0.4)',
              }}
            >
              Reset Password
            </Button>
          </Form.Item>
        </Form>

        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <Text style={{ fontSize: 11, color: 'var(--wl-text-muted)' }}>
            &copy; {new Date().getFullYear()} WeCircle
          </Text>
        </div>
      </div>
    </AuthCard>
  );
};

export default ResetPassword;
